import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { $accountByIdStore, $profileFeatureStore } from '@lib/store';
import { useAccountFlow } from '@shared/hooks';
import { ValidateUtils } from '@shared/utils';
import type { AccountByIdResponse } from '@shared/api/rest/accounts';
import { BaseInput, Button, ErrorMessage, Modal } from '@shared/widgets';

interface IForm {
  label: string;
  costCentre: string;
  category: string;
}

interface Props {
  show: boolean;
  onClose: () => void;
  account: AccountByIdResponse;
  initial: Partial<IForm>;
}

export const ModalEditDetails = (props: Props) => {
  const { id } = useParams();

  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const { control, formState, handleSubmit, reset } = useForm<IForm>({
    defaultValues: { label: props.initial.label || '', costCentre: props.initial.costCentre || '', category: props.initial.category || '' },
  });

  const closeHandler = () => {
    props.onClose();
    reset({ label: props.initial.label || '', costCentre: props.initial.costCentre || '', category: props.initial.category || '' });
  };
  const submitHandler = async (data: IForm) => {
    await $accountByIdStore.events.updateDetails({
      request: { accountId: props.account.id, label: data.label, costCentre: data.costCentre, category: data.category },
      uId: props.account.id,
    });
    props.onClose();
  };

  const { accountFlow } = useAccountFlow(profileFeatureStore.data?.accountFlows, id ? parseInt(id) : undefined);
  if (!accountFlow?.editAccountLabel && !accountFlow?.editCostCentre && !accountFlow?.editAccountCategory) return null;
  return (
    <Modal isOpen={props.show} onClose={closeHandler} title='Account details'>
      <form className='flex flex-col gap-5' onSubmit={handleSubmit(submitHandler)}>
        {accountFlow?.editAccountLabel && (
          <Controller
            name='label'
            control={control}
            rules={{ required: ValidateUtils.requiredErrorMessage }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} placeholder='Account label' />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        {accountFlow?.editCostCentre && (
          <Controller
            name='costCentre'
            control={control}
            rules={{ required: ValidateUtils.requiredErrorMessage }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} placeholder='Cost centre' />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        {accountFlow?.editAccountCategory && (
          <Controller
            name='category'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} placeholder='Account category' />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        <Button className='mt-7' fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
          Save changes
        </Button>
      </form>
    </Modal>
  );
};
